import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { CREDITS_CHANGED_EVENT } from "@/lib/credits-events";
import { parseVoiceCreditSummary, type VoiceCreditSummary } from "@/lib/voice-credits";

export interface VoiceCreditMovement {
  id: number;
  kind: string;
  amount_cop: number;
  balance_after_cop: number;
  minutes: number | null;
  created_at: string;
}

export interface VoiceCreditsState {
  hasVoiceAccount: boolean;
  summary: VoiceCreditSummary | null;
  movements: VoiceCreditMovement[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const RECENT_MOVEMENTS = 10;

export async function hasActiveVoiceAccount(orgId: string): Promise<boolean> {
  const { data } = await supabase
    .from("voice_assistants_retell_accounts")
    .select("id")
    .eq("organization_id", orgId)
    .eq("is_active", true)
    .eq("sync_status", "synced")
    .limit(1)
    .maybeSingle();
  return !!data;
}

export function useVoiceCredits(orgId: string | null, enabled: boolean, withMovements = false): VoiceCreditsState {
  const [hasVoiceAccount, setHasVoiceAccount] = useState(false);
  const [summary, setSummary] = useState<VoiceCreditSummary | null>(null);
  const [movements, setMovements] = useState<VoiceCreditMovement[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!orgId || !enabled) {
      setHasVoiceAccount(false);
      setSummary(null);
      setMovements([]);
      setLoading(false);
      return;
    }
    const account = await hasActiveVoiceAccount(orgId);
    setHasVoiceAccount(account);
    if (!account) {
      setSummary(null);
      setMovements([]);
      setLoading(false);
      return;
    }
    const [summaryRes, ledgerRes] = await Promise.all([
      supabase.rpc("voice_credit_summary" as never, { p_org: orgId } as never),
      withMovements
        ? supabase
          .from("voice_credit_ledger" as never)
          .select("id, kind, amount_cop, balance_after_cop, minutes, created_at" as never)
          .eq("organization_id" as never, orgId as never)
          .order("created_at" as never, { ascending: false })
          .limit(RECENT_MOVEMENTS)
        : Promise.resolve({ data: [], error: null }),
    ]);
    setSummary(summaryRes.error ? null : parseVoiceCreditSummary(summaryRes.data));
    setMovements(((ledgerRes.data ?? []) as unknown as VoiceCreditMovement[]).map((row) => ({
      ...row,
      amount_cop: Number(row.amount_cop),
      balance_after_cop: Number(row.balance_after_cop),
      minutes: row.minutes === null || row.minutes === undefined ? null : Number(row.minutes),
    })));
    setLoading(false);
  }, [orgId, enabled, withMovements]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!orgId || !enabled || typeof window === "undefined") return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ orgId?: string }>).detail;
      if (!detail?.orgId || detail.orgId === orgId) void refresh();
    };
    window.addEventListener(CREDITS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, handler);
  }, [orgId, enabled, refresh]);

  return { hasVoiceAccount, summary, movements, loading, refresh };
}
